/* # ReplyForm Component
   - A form component for adding replies to forum topics
   - Removed file size display as requested by user
*/
import { useState } from 'react';
import { LuSend, LuPaperclip, LuX } from 'react-icons/lu';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../hooks/use-toast';
import { createTopicReplyNotifications, createMentionNotification } from '../../utils/notificationHelpers';
import { extractMentionNames, validateMentionedUsers, storeMentions } from '../../utils/mentionUtils';
import MentionsAutocomplete from './MentionsAutocomplete';

export default function ReplyForm({ topicId, topicTitle, onReplyAdded }) {
  const { user } = useAuth();
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Add new files to existing selection
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMentions = async (text, replyId) => {
    try {
      const usernames = extractMentionNames(text);
      if (usernames.length === 0) return;

      const validUsers = await validateMentionedUsers(usernames);
      if (validUsers.length === 0) return;

      // Store mentions in database with reply entity
      await storeMentions(validUsers, user.id, 'reply', replyId);

      // Send notifications - use topicId for proper linking to the topic detail page
      for (const mentionedUser of validUsers) {
        if (Number(mentionedUser.id) === Number(user.id)) continue;

        await createMentionNotification(
          mentionedUser.id,
          user.name,
          'reply', // Content type
          Number(topicId), // Link to topic detail page
          topicTitle || 'a forum topic',
          text
        ).catch((e) => console.error('Failed to send mention notification:', e));
      }
    } catch (e) {
      console.error('[Mentions] Error processing mentions:', e);
    }
  };

  const uploadAttachments = async (replyId) => {
    if (selectedFiles.length === 0) return;

    try {
      for (const file of selectedFiles) {
        // Upload file to storage
        const { data: fileId, error: uploadError } = await window.ezsite.apis.upload({
          filename: file.name,
          file: file
        });

        if (uploadError) throw uploadError;

        // Get file URL
        const { data: fileUrl, error: urlError } = await window.ezsite.apis.getUploadUrl(fileId);
        if (urlError) throw urlError;

        // Save attachment record
        const { error: attachmentError } = await window.ezsite.apis.tableCreate(71361, {
          reply_id: Number(replyId),
          file_url: fileUrl,
          file_name: file.name,
          file_size: file.size,
          file_type: file.type || 'application/octet-stream',
          uploaded_at: new Date().toISOString(),
          uploaded_by: user.id
        });

        if (attachmentError) throw attachmentError;
      }
    } catch (error) {
      console.error('Failed to upload attachments:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!body.trim()) {
      toast({ title: 'Error', description: 'Reply cannot be empty', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const now = new Date().toISOString();
      const { data: replyData, error: replyError } = await window.ezsite.apis.tableCreate(70653, {
        topic_id: Number(topicId),
        author_id: user.id,
        body: body,
        parent_reply_id: null,
        created_at: now,
        updated_at: now
      });

      if (replyError) throw replyError;

      // Extract reply ID - API may return it in different formats
      let replyId = null;
      if (typeof replyData === 'number') {
        replyId = replyData;
      } else if (replyData?.id) {
        replyId = replyData.id;
      } else if (replyData?.ID) {
        replyId = replyData.ID;
      }

      // Upload attachments if any
      if (replyId && selectedFiles.length > 0) {
        try {
          await uploadAttachments(replyId);
        } catch (attachError) {
          console.error('Attachment upload failed:', attachError);
          toast({
            title: 'Warning',
            description: 'Reply posted but some attachments failed to upload',
            variant: 'destructive'
          });
        }
      }

      // Update topic stats
      const { data: topicData } = await window.ezsite.apis.tablePage(70652, {
        PageNo: 1,
        PageSize: 1,
        Filters: [{ name: 'id', op: 'Equal', value: Number(topicId) }]
      });

      if (topicData?.List?.[0]) {
        const topic = topicData.List[0];
        await window.ezsite.apis.tableUpdate(70652, {
          id: Number(topicId),
          reply_count: (topic.reply_count || 0) + 1,
          last_activity_at: now
        });

        // Notify participants
        createTopicReplyNotifications(replyData, topic, user.name);

        // Handle mentions - pass topicId for notification linking
        if (replyId) {
          await handleMentions(body, replyId);
        } else {
          console.warn('[ReplyForm] Could not extract reply ID for mentions:', replyData);
        }
      }

      toast({ title: 'Success', description: 'Reply posted successfully' });
      setBody('');
      setSelectedFiles([]);
      onReplyAdded();
    } catch (error) {
      console.error('Failed to post reply:', error);
      toast({ title: 'Error', description: 'Failed to post reply', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Add a Reply</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <MentionsAutocomplete
          value={body}
          onChange={setBody}
          placeholder="Write your reply... Type @ to mention someone"
          rows={4} />

        {/* File attachments section */}
        {selectedFiles.length > 0 &&
        <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Attachments ({selectedFiles.length})</p>
            <div className="space-y-2">
              {selectedFiles.map((file, index) =>
            <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <LuPaperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                  </div>
                  <button
                type="button"
                onClick={() => removeFile(index)}
                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                title="Remove file">
                    <LuX className="w-4 h-4" />
                  </button>
                </div>
            )}
            </div>
          </div>
        }

        <div className="flex items-center justify-between">
          {/* File upload button */}
          <label className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer">
            <LuPaperclip className="w-4 h-4" />
            <span className="text-sm font-medium">Attach Files</span>
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              disabled={loading} />
          </label>

          <button
            type="submit"
            disabled={loading || !body.trim()}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ?
            <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Posting...
              </> :

            <>
                <LuSend className="w-4 h-4" />
                Post Reply
              </>
            }
          </button>
        </div>
      </form>
    </div>);

}